import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cls from 'classnames';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { ReactComponent as ChevronDown } from 'assets/svg/chevron-down.svg';

import container from 'containers/NetworkMembershipContainer';
import SpecialsBadge from 'components/shared/ui/SpecialsBadge';

import { useDimension } from 'hooks/useDimensions';

import theme from 'utils/theme';
import { getPriceInfo } from 'utils/priceInfoHelper';

const GolfClubCourseList = ({
  location,
  golfClubCourses,
  isFetchingGolfClubCoursesDone,
  setPreviousUrl,
  showBadges,
  wrapperStyles,
  handleCourseClick,
}) => {
  const { width } = useDimension();
  const courseContainerRef = useRef();
  const courseScrollRef = useRef();
  const history = useHistory();
  const isWebView = useSelector(({ app }) => app?.isWebView);

  const onCourseClick = (course) => {
    if (!course?.hierarchizedUrl) return;
    if (handleCourseClick) handleCourseClick(course);
    if (isWebView) {
      window.location.href = `${process.env.REACT_APP_WEBVIEW_REDIRECT_ORIGIN}/explore${course.hierarchizedUrl}`;
    } else {
      setPreviousUrl(location.pathname);
      history.push(`/explore${course.hierarchizedUrl}`);
    }
  };

  const handleCoursesScroll = (e, scrollParent) => {
    if (!courseContainerRef.current || !courseScrollRef.current) return;
    if (scrollParent) {
      courseContainerRef.current.scrollLeft = e.target.scrollLeft;
    } else {
      courseScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const renderImage = (course) => (
    <div className="relative">
      <img src={course.photoMediumUrl} className="w-full h-40 object-cover object-center" alt="course-thumb" />
      {showBadges && (
        <div className="absolute left-2 bottom-2 flex gap-2">
          {course.stats.hasHotDeals && (
            <SpecialsBadge />
          )}
          {course?.hasNetworkMembership && (
            <div className="px-2 font-primary font-medium flex items-center justify-center h-6 text-3.5 leading-4 -tracking-0.4 bg-white border border-light-grey-four rounded">
              <span className="font-bold text-membership-blue">Benefits&nbsp;</span>
              <span className="text-mild-grey">with Club</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCourseDetails = (course) => {
    const price = {
      ...getPriceInfo(course, ['minRate', '', 'additionalFeesForMinRate']),
      ...getPriceInfo(course, ['maxRate', '', 'additionalFeesForMaxRate']),
      ...getPriceInfo(course, ['maxAdditionalFees']),
    };

    return (
      <div className="p-4 pt-3 flex flex-col flex-1 min-h-50">
        {course?.name && <h4 className="font-primary font-bold text-4 leading-5 text-dark w-full truncate">{course.name}</h4>}
        {course?.addressState && course?.addressCity && <p className="mt-2 font-primary-two text-3.5 leading-4 -tracking-0.2 text-grey-3">{`${course?.addressCity}, ${course?.addressState}`}</p>}
        {price.minRate && (
          <p className="mt-3 font-primary font-black text-5 leading-5 -tracking-0.4 text-dark">
            {`${price.minRateLabel}${price.minRate !== price.maxRate ? ` - ${price.maxRateLabel}` : ''}`}
          </p>
        )}
        {!!price.maxAdditionalFees && (
          <div className="mt-1.5 flex items-center gap-1">
            <p className="font-primary text-2.5 leading-3 text-dark">
              Includes fee up to
              {' '}
              <span className="font-semibold">
                {price.maxAdditionalFeesLabel}
              </span>
            </p>
          </div>
        )}
        <div className="mt-auto space-y-4">
          {course?.networkMembershipDiscountPercentage && (
            <div className="w-fit mt-3 font-primary text-3.5 leading-4 -tracking-0.4 px-2 flex items-center h-8 rounded-1 border border-light-grey-four">
              <span className="font-bold text-membership-blue">{`Save an extra ${course.networkMembershipDiscountPercentage}%`}</span>
              <span className="font-medium text-mild-grey">&nbsp;with Club</span>
              <ChevronDown transform="rotate(90)" style={{ transform: 'rotate(90deg)' }} className="w-4 h-4 fill-current text-dark" />
            </div>
          )}
          {course?.hierarchizedUrl && (
            <button
              type="button"
              className="font-primary font-bold w-full h-8 border border-dark rounded-1 flex justify-center items-center text-4 leading-4 -tracking-0.4 text-dark focus:outline-none"
            >
              View course
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCourseListSkeleton = Array(3).fill().map((_, index) => {
    const key = `course-skeleton-${index}`;
    return (
      <SkeletonTheme
        key={key}
        color={theme.colors['white-one']}
        highlightColor={theme.colors.white}
      >
        <div className="w-68 rounded-2 overflow-hidden">
          <Skeleton width={272} height={200} />
          <div className="h-50 flex flex-col p-4 pt-3">
            <div className="leading-5 flex">
              <Skeleton width={215} height={20} />
            </div>
            <div className="mt-1 flex leading-4">
              <Skeleton width={89} height={16} />
            </div>
            <div className="mt-3 h-5 flex leading-5">
              <Skeleton width={120} height={20} />
            </div>
            <div className="mt-auto flex leading-8">
              <Skeleton height={32} className="w-full" />
            </div>
          </div>
        </div>
      </SkeletonTheme>
    );
  });

  const renderCourseList = (
    golfClubCourses?.map((course, i) => (
      <div
        key={course.id}
        className={cls('flex-1 flex flex-col border border-light-grey-four rounded-2 overflow-hidden min-w-68 cursor-pointer', {
          'mr-4 md:mr-10 lg:m-0': i === golfClubCourses.length - 1,
        })}
        onClick={(e) => {
          e.stopPropagation();
          onCourseClick(course);
        }}
        aria-hidden
      >
        {renderImage(course)}
        {renderCourseDetails(course)}
      </div>
    ))
  );

  return (
    <div className="overflow-hidden">
      <div
        ref={courseContainerRef}
        className={cls('mt-6 flex gap-4 overflow-x-auto pl-4 md:pl-10 lg:m-0 lg:p-0', {
          'no-scrollbar': width <= 1024,
          'scrollbar-thin lg:pb-4': width >= 1024,
        }, wrapperStyles)}
        onScroll={(e) => handleCoursesScroll(e, false)}
      >
        {isFetchingGolfClubCoursesDone ? renderCourseList : renderCourseListSkeleton}
      </div>
      {courseContainerRef.current && (
        <div ref={courseScrollRef} className="mx-4 scrollbar-thin overflow-x-auto md:mx-10 lg:hidden" onScroll={(e) => handleCoursesScroll(e, true)}>
          <div className="h-4" style={{ width: `${courseContainerRef.current.scrollWidth - (width >= 768 ? 80 : 32)}px` }} />
        </div>
      )}
    </div>
  );
};

GolfClubCourseList.defaultProps = {
  golfClubCourses: [],
  showBadges: false,
  wrapperStyles: '',
  handleCourseClick: null,
};

GolfClubCourseList.propTypes = {
  golfClubCourses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    photoMediumUrl: PropTypes.string,
    hierarchizedUrl: PropTypes.string,
    addressCity: PropTypes.string,
    addressState: PropTypes.string,
    minRate: PropTypes.number,
    maxRate: PropTypes.number,
    additionalFees: PropTypes.number,
    symbol: PropTypes.string,
    currency: PropTypes.string,
    showSymbol: PropTypes.bool,
    stats: PropTypes.shape({
      hasHotDeals: PropTypes.bool,
    }),
    hasNetworkMembership: PropTypes.bool,
    networkMembershipDiscountPercentage: PropTypes.number,
  })),
  isFetchingGolfClubCoursesDone: PropTypes.bool.isRequired,
  showBadges: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  setPreviousUrl: PropTypes.func.isRequired,
  wrapperStyles: PropTypes.string,
  handleCourseClick: PropTypes.func,
};

export default container(GolfClubCourseList);
